import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Dealer } from '@shared/models/dealer';
import { DealerEditComponent } from '@routes/dealers/dealer-edit/dealer-edit.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { SpinnerService } from '@shared/services/spinner.service';
import { AuthService } from '@core/auth/auth.service';
import { ApiService } from '@shared/apis';
import Utils from '@shared/utils/utils';
import { DealerContactEditComponent } from '@routes/dealers/dealer-contact-edit/dealer-contact-edit.component';
import Swal from 'sweetalert2';
import { DealerLocationComponent } from '@routes/dealers/dealer-location/dealer-location.component';
import Roles from '@shared/constants/roles';
import { Constants } from '@shared/constants/constants';
import { Company } from '@shared/enums/company';
import Role from '@shared/constants/role';
import { PageEvent } from '@shared/models/page-event';
import Configs from '@shared/constants/configs';
import * as _ from 'lodash-es';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-dealer-detail',
  templateUrl: './dealer-detail.component.html',
  styleUrls: ['./dealer-detail.component.scss'],
})
export class DealerDetailComponent implements OnInit {
  @Input() dealerDetail: Dealer;
  @Output() detailUpdateEvent = new EventEmitter();
  @Output() changeView = new EventEmitter();

  bsModalRef: BsModalRef;

  concealedType = Constants.concealedTypeType;

  bpType: any;

  view = 'CONTACT';
  showNew = false;
  isAdmin = false;
  isEditable = false;
  isContactManage = false;
  isDealerSupervisor = false;
  isDealerStaff = false;
  isAdminViewerRole: boolean;
  company;
  companies = Company;
  isHq = false;
  user;
  isContactEditable = true;

  currentPage = 0;
  totalPages = 0;
  totalItems = null;
  readonly configs = Configs;
  defaultPagination = _.cloneDeep(this.configs.pagination);
  itemsPerPage = this.defaultPagination.defaultSize;
  boundHeaderClickEvent = false;
  dealerContacts;
  private sortOrders = [];
  @ViewChild('listContainer') private listContainer;
  userDetails: any = null;

  constructor(
    private modalService: BsModalService,
    private spinnerService: SpinnerService,
    private authService: AuthService,
    private apiService: ApiService
  ) {
    this.showNew = this.authService.hasType(Roles.dealerNewView);
    this.isAdmin = this.authService.hasType(Roles.dealerNewView);
    this.company = this.authService.getCurrentCompany();
    this.user = this.authService.getUser();
    this.isDealerSupervisor = this.authService.hasRoleInCompany(
      Role.secaDealerSuperVisor
    );
    this.isDealerStaff = this.authService.hasRoleInCompany(
      Role.secaDealerStaff
    );
    this.isAdminViewerRole = this.authService.checkHasAdminViewerRole();
    this.isHq = this.authService.isHq();
  }

  @Input() set dealerInfo(value) {
    if (!Utils.isEmpty(value)) {
      this.dealerDetail = value;
      this.dealerContacts = value.contacts;
      this.view = 'CONTACT';
      this.bpType = value.bpTypeName
        ? `[${value.bpType}] ${value.bpTypeName}`
        : value.bpType;

      this.userDetails = { bpList: [value.bpNo], isDealer: true };
      if (value.hq) {
        this.apiService.dealer
          .getDealerByHqBpNo(value.bpNo)
          .subscribe((res) => {
            const bpList = res || [];
            bpList.push(value.bpNo);
            this.userDetails = {
              bpList: bpList,
              hq: value.hq,
              isDealer: true,
              hqBpNo: value.bpNo,
            };
          });
      }
    }
  }

  private loadDealerUserInfo(contacts): void {
    // userDetails 배열 초기화
    this.userDetails = [];

    // email을 username으로 사용하여 user 정보 조회
    const requests = contacts
      .filter((contact) => contact && contact.email)
      .map((contact) =>
        this.apiService.user.getUser(contact.email).pipe(
          catchError((error) => {
            // user를 찾을 수 없는 경우 null 반환
            console.log('User not found for email:', contact.email);
            return of(null);
          })
        )
      );

    if (requests.length === 0) {
      return;
    }

    // 모든 요청을 병렬로 실행
    forkJoin(requests).subscribe((users: any[]) => {
      // 성공한 user들만 필터링하여 추가
      this.userDetails = users.filter((user: any) => user && user._id);
    });
  }

  ngOnInit(): void {
    this.isEditable =
      this.isAdmin ||
      Company.Sea === this.company ||
      ([Company.SeaIm, Company.Seca].includes(this.company) &&
        this.isDealerSupervisor);
    this.isContactManage = true;
    this.isContactEditable = this.user.roles.filter((r) => r);
    this.dealerContacts = this.dealerDetail.contacts;
    if (!this.showNew) {
      const { userType, hq } = this.authService.getToken();
      if (Constants.userType.dealer === userType && hq) {
        this.showNew = true;
      }
    }
    if (this.dealerDetail.contacts) {
      this.bindTableHeaderClickEvent();
    }
  }

  modalOpen() {
    const initialState = {
      bpNo: this.dealerDetail.bpNo,
      title: 'Dealer Edit',
      dealerInfo: this.dealerDetail,
      buttonLabel: 'Update',
    };

    this.bsModalRef = this.modalService.show(DealerEditComponent, {
      initialState,
      class: 'modal-lg',
    });
    this.bsModalRef.content.onClose.subscribe((result) => {
      if (result) {
        this.detailUpdateEvent.emit(result);
      }
    });
  }

  openPopup(view) {
    if (view === 'CONTACT') {
      this.openContact();
    } else if (view === 'LOCATION') {
      this.openAlias();
    } else {
      return;
    }
  }

  openContact(contactDetail?) {
    const initialState = {
      id: contactDetail ? contactDetail._id : null,
      bpNo: this.dealerDetail.bpNo,
      hq: this.dealerDetail.hq,
      title: 'Contact Information',
      contactInfo: contactDetail,
      buttonLabel: contactDetail ? 'Update' : 'Create',
    };

    this.bsModalRef = this.modalService.show(DealerContactEditComponent, {
      initialState,
      class: 'modal-lg',
    });
    this.bsModalRef.content.onClose.subscribe((result) => {
      if (result) {
        this.detailUpdateEvent.emit(result);
      }
    });
  }

  deleteContact(bpNo, contactId) {
    Swal.fire({
      title: 'Are you sure to delete the Dealer Contact?',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        this.apiService.dealer.deleteContact(bpNo, contactId).subscribe(
          (res) => {
            if (res) {
              this.detailUpdateEvent.emit(res);
            }
            Swal.fire('Deleted!', '', 'success');
            this.dealerDetail = res;
          },
          (err) => Utils.showError(err)
        );
      } else if (result.isDenied) {
        Swal.fire('Canceled!', '', 'info');
      }
    });
  }

  openAlias(locationAlias?) {
    const initialState = {
      id: locationAlias ? locationAlias._id : null,
      bpNo: this.dealerDetail.bpNo,
      title: 'Location Alias',
      locationAlias,
      buttonLabel: locationAlias ? 'Update' : 'Create',
    };

    this.bsModalRef = this.modalService.show(DealerLocationComponent, {
      initialState,
      class: 'modal-lg',
    });
    this.bsModalRef.content.onClose.subscribe((result) => {
      if (result) {
        this.detailUpdateEvent.emit(result);
      }
    });
  }

  deleteLocationAlias(bpNo, locationAliasId) {
    Swal.fire({
      title: 'Are you sure to delete the Location Alias?',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        this.apiService.dealer
          .deleteLocationAlias(bpNo, locationAliasId)
          .subscribe(
            (res) => {
              Swal.fire('Deleted!', '', 'success');
              this.dealerDetail = res;
            },
            (err) => Utils.showError(err)
          );
      } else if (result.isDenied) {
        Swal.fire('Canceled!', '', 'info');
      }
    });
  }

  showSubItem(view) {
    this.view = view;
  }

  sortDealerContact(pageEvent) {
    console.log('PageEvent =', pageEvent);
    if (
      !this.dealerDetail?.contacts &&
      this.dealerDetail.contacts?.length < 2
    ) {
      return;
    }
    console.log('this.dealerDetail.contacts =', this.dealerDetail.contacts);
    const sorts = pageEvent.sort.split(',');
    console.log('sorts =', sorts);
    let dealerContacts = _.cloneDeep(this.dealerContacts);
    sorts.forEach((sort) => {
      const sortType = sort.substring(0, 1) === '-' ? -1 : 1;
      const sortField =
        sort.substring(0, 1) === '-' ? sort.substring(1, sort.length) : sort;
      console.log('sortField = ', sortField);
      dealerContacts = dealerContacts.sort((dc1, dc2) =>
        dc1[sortField] > dc2[sortField] ? 1 * sortType : -1 * sortType
      );
    });
    this.dealerContacts = dealerContacts;
    console.log('this.dealerContacts after sorting =', this.dealerContacts);
  }

  private bindTableHeaderSortRemove() {
    if (!this.listContainer) {
      return;
    }

    const headers =
      this.listContainer.nativeElement.querySelectorAll('thead th');

    Array.from(headers).forEach((h: any) => {
      const sf = h.getAttribute('data-sort-fields');
      if (!sf) {
        return;
      }

      // remove not sorted
      if (h.classList.contains('asc')) {
        h.classList.remove('asc');
      }
      if (h.classList.contains('desc')) {
        h.classList.remove('desc');
      }
    });
    this.sortOrders = [];
  }

  private bindTableHeaderClickEvent() {
    setTimeout(() => {
      const headers =
        this.listContainer.nativeElement.querySelectorAll('thead th');

      Array.from(headers).forEach((header) => {
        // @ts-ignore
        header.addEventListener('click', (e) => {
          if (this.totalItems === 0) {
            return;
          }

          const target = e.target;
          const sortFields = target.dataset.sortFields;
          console.log(
            'sortFields =',
            sortFields,
            ', sortOrders =',
            this.sortOrders
          );
          this.changeSortOrders(sortFields);

          // update icons
          Array.from(headers).forEach((h: any) => {
            const sf = h.getAttribute('data-sort-fields');
            if (!sf) {
              return;
            }

            let found = false;
            this.sortOrders.forEach((so) => {
              if (so.fields.join(',') === sf.replace(/\s/, '')) {
                found = true;
                if (so.order === 'asc') {
                  // sort by asc
                  if (h.classList.contains('desc')) {
                    h.classList.remove('desc');
                  }
                  if (!h.classList.contains('asc')) {
                    h.classList.add('asc');
                  }
                } else {
                  // sort by desc
                  if (h.classList.contains('asc')) {
                    h.classList.remove('asc');
                  }
                  if (!h.classList.contains('desc')) {
                    h.classList.add('desc');
                  }
                }
              }
            });

            if (!found) {
              // remove not sorted
              if (h.classList.contains('asc')) {
                h.classList.remove('asc');
              }
              if (h.classList.contains('desc')) {
                h.classList.remove('desc');
              }
            }
          });
        });
      });

      this.boundHeaderClickEvent = true;
    }, 500);
  }

  private changeSortOrders(sortFields) {
    // sort logic (1st) -> asc (2nd) -> desc (3rd) -> no sort
    if (!sortFields) {
      return;
    }
    // this.bindTableHeaderSortRemove();

    let founded = false;
    let orders = [];

    this.sortOrders.forEach((sortOrder) => {
      const fields = sortOrder.fields.join(',');
      if (fields === sortFields.replace(/\s/, '')) {
        founded = true;
        if (sortOrder.order === 'asc') {
          sortOrder.order = 'desc';
          orders.push(sortOrder);
        }
      } else {
        orders.push(sortOrder);
      }
    });

    if (!founded) {
      orders = [
        {
          fields: sortFields.split(',').map((item) => item.trim()),
          order: 'asc',
        },
      ];
    }
    this.sortOrders = orders;

    const pageEvent = new PageEvent(this.currentPage, this.itemsPerPage);
    pageEvent.sort = this.parseSortOrders();
    this.sortDealerContact(pageEvent);
  }

  private parseSortOrders() {
    const sorts = [];
    this.sortOrders.forEach((sortOrder) => {
      const order = sortOrder.order;
      _.get(sortOrder, 'fields', []).forEach((field) => {
        sorts.push((order === 'desc' ? '-' : '') + field);
      });
    });

    return sorts.join(',');
  }
}
